import { ResultRecord, ServiceResponse } from "@rsm-hcd/javascript-core";
import { ServiceFactory as RsmServiceFactory } from "@rsm-hcd/javascript-react";
import axios, { AxiosError } from "axios";
import _ from "lodash";
import { PromiseUtils } from "utilities/promise-utils";
import { RouteUtils } from "utilities/route-utils";
import ServiceUtils from "utilities/service-utils";
import ServiceResponseFactory from "utilities/services/service-response-factory";
import { CollectionUtils } from "./../collection-utils";
import { PatchService } from "./service-hook-factory";

// -----------------------------------------------------------------------------------------
// #region Types
// -----------------------------------------------------------------------------------------

type RecordId = string | number | null | undefined;

export interface RecordType {
    id?: RecordId;
    toJS: () => unknown;
}

/**
 * Type defining the service function to bulk creating the supplied resource type
 */
export type BulkCreateService<TRecord> = (
    records?: Array<TRecord>
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for bulk updating the supplied resource type
 */
export type BulkUpdateService<TRecord, TPathParams> = (
    records: Array<TRecord>,
    pathParams: TPathParams
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for creating the supplied resource type
 */
export type CreateService<TRecord> = (
    record?: TRecord
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for creating the supplied resource type from a set
 * of parameters in the request body.
 */
export type CreateFromPostParamsService<TRecord, TPostParams> = (
    requestData?: TPostParams,
    signal?: AbortSignal
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for deleting the supplied resource
 */
export type DeleteService = (
    id: number | string,
    pathParams?: any
) => Promise<ServiceResponse<Boolean>>;

/**
 * Type defining the service function for deleting resources by path and query params.
 */
export type BulkDeleteService<TPathParams, TQueryParams> = (
    pathParams?: TPathParams,
    queryParams?: TQueryParams
) => Promise<ServiceResponse<Boolean>>;

// TODO: Move to open source.
// See https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/55 for more information.
/**
 * Type defining the service function for batch listing resources by supplied type
 */
export type BatchedListService<TRecord, TQueryParams> = (
    queryParams: TQueryParams,
    property: keyof TQueryParams,
    size: number
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for listing resources by supplied type
 *
 * @param queryParams Object with query parameters to be appended to the endpoint route
 * @param signal AbortSignal to be used for aborting the request
 */
export type BatchedListServiceWithSignal<TRecord, TQueryParams> = (
    queryParams: TQueryParams,
    property: keyof TQueryParams,
    size: number,
    signal?: AbortSignal
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for listing resources by supplied type
 */
export type ListService<TRecord, TQueryParams> = (
    queryParams?: TQueryParams
) => Promise<ServiceResponse<TRecord>>;

// TODO: Move to open source.
// See https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/55 for more information.
/**
 * Type defining the service function for batch listing nested resources by supplied type
 */
export type NestedBatchedListService<TRecord, TPathParams, TQueryParams> = (
    pathParams: TPathParams,
    queryParams: TQueryParams,
    property: keyof TQueryParams,
    size: number
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for creating the supplied resource type when resource is nested
 */
export type NestedCreateService<TRecord, TPathParams> = (
    record: TRecord,
    pathParams: TPathParams
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for creating the supplied nested resource type
 *  from a setof parameters in the request body with a set of path params to the resource
 */
export type NestedCreateFromPostParamsService<
    TRecord,
    TPostParams,
    TPathParams,
> = (
    pathParams: TPathParams,
    requestData?: TPostParams,
    signal?: AbortSignal
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for listing resources by supplied type when resource is nested
 */
export type NestedListService<TRecord, TPathParams, TQueryParams> = (
    pathParams: TPathParams,
    queryParams?: TQueryParams
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for updating the supplied resource type
 */
export type UpdateService<TRecord, TPathParams, TQueryParams = undefined> = (
    record: TRecord,
    pathParams?: TPathParams,
    queryParams?: TQueryParams
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for updating the supplied resource type
 *
 * @param record Record to be created
 * @param pathParams Object with path parameters to be replaced in the endpoint route
 * @param queryParams Object with query parameters to be added to the endpoint route
 * @param signal AbortSignal to be used for aborting the request
 */
export type UpdateServiceWithSignal<
    TRecord,
    TPathParams,
    TQueryParams = undefined,
> = (
    record: TRecord,
    pathParams?: TPathParams,
    queryParams?: TQueryParams,
    signal?: AbortSignal
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for updating the supplied resource type from a set of request
 * body parameters.
 *
 * @param requestData Object with parameters to be sent in the request body
 * @param pathParams Object with path parameters to be replaced in the endpoint route
 * @param queryParams Object with query parameters to be added to the endpoint route
 * @param signal AbortSignal to be used for aborting the request
 */
export type UpdateFromPutParamsService<
    TRecord,
    TPutParams,
    TPathParams,
    TQueryParams = undefined,
> = (
    requestData?: TPutParams,
    pathParams?: TPathParams,
    queryParams?: TQueryParams,
    signal?: AbortSignal
) => Promise<ServiceResponse<TRecord>>;

/**
 * Type defining the service function for streaming the supplied resource type.
 */
export type StreamServiceWithSignal<
    TRecord extends RecordType,
    TPathParams = undefined,
> = (
    record: TRecord,
    pathParams?: TPathParams,
    signal?: AbortSignal
) => ReadableStream<ServiceResponse<TRecord>>;

// #endregion Types

// ---------------------------------------------------------------------------------------------
// #region Public Functions
// ---------------------------------------------------------------------------------------------

/**
 * Factory to encapsulate common service function logic
 */
const ServiceFactory = {
    ...RsmServiceFactory,

    /**
     * Creates Bulk Service Create function for the supplied resource type
     *
     * @template TRecord
     * @param recordType
     * @param baseEndpoint
     * @returns
     */
    bulkCreate: <TRecord extends RecordType>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): BulkCreateService<TRecord> => {
        return async (records?: Array<TRecord>) =>
            await _bulkCreate<TRecord>(recordType, baseEndpoint, records);
    },

    // TODO: Move to open source.
    // See https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/55 for more information.
    /**
     * Creates batch Service List function for the supplied resource type
     *
     * ### Recommendation
     * Use `nestedList` when route is nested!
     *
     * @param recordType
     * @param baseEndpoint
     * @param property array property for which to batch
     * @param size maximum size of each batched request
     */
    batchedList: <TRecord, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): BatchedListServiceWithSignal<TRecord, TQueryParams> => {
        return async (
            queryParams: TQueryParams,
            property: keyof TQueryParams,
            size: number,
            signal?: AbortSignal
        ) => {
            return _batchedList(
                recordType,
                baseEndpoint,
                property,
                size,
                queryParams,
                signal
            );
        };
    },

    /**
     * Creates a Service Create function for the supplied resource type, created with a set
     * of parameters in the request body.
     *
     * @param recordType
     * @param resourceEndpoint
     */
    createFromPostParams: <TRecord extends RecordType, TPostParams>(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): CreateFromPostParamsService<TRecord, TPostParams> => {
        return async (requestData?: TPostParams, signal?: AbortSignal) =>
            _createFromPostParams<TRecord, TPostParams>(
                recordType,
                resourceEndpoint,
                requestData,
                signal
            );
    },

    /**
     * Creates conventional Service Delete function for the supplied resource type
     * @param recordType
     * @param resourceEndpoint
     */
    delete<TPathParams extends any>(resourceEndpoint: string): DeleteService {
        return async (id: number | string, pathParams?: TPathParams) =>
            await _delete<TPathParams>(id, resourceEndpoint, pathParams);
    },

    /**
     * Creates conventional Service Delete function for resources deleted by query params.
     * @param resourceEndpoint
     */
    bulkDelete<TPathParams extends any, TQueryParams extends any>(
        resourceEndpoint: string
    ): BulkDeleteService<TPathParams, TQueryParams> {
        return async (pathParams?: TPathParams, queryParams?: TQueryParams) =>
            await _bulkDelete<TPathParams, TQueryParams>(
                resourceEndpoint,
                pathParams,
                queryParams
            );
    },

    // TODO: Move to open source.
    // See https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/55 for more information.
    /**
     * Creates batched Service List function for the supplied resource type
     * when the resource is nested
     *
     * @param recordType
     * @param baseEndpoint
     * @param property array property for which to batch
     * @param size maximum size of each batched request
     */
    nestedBatchedList: <TRecord, TPathParams, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): NestedBatchedListService<TRecord, TPathParams, TQueryParams> => {
        return async (
            pathParams: TPathParams,
            queryParams: TQueryParams,
            property: keyof TQueryParams,
            size: number
        ) => {
            return _batchedList(
                recordType,
                baseEndpoint,
                property,
                size,
                queryParams,
                pathParams
            );
        };
    },

    /**
     * Creates a Service Create function for the supplied nested resource type, created
     * with a set of parameters in the request body and a set of parameters to set in
     * the path to the resource
     *
     * @param recordType
     * @param resourceEndpoint
     */
    nestedCreateFromPostParams: <
        TRecord extends RecordType,
        TPostParams,
        TPathParams,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): NestedCreateFromPostParamsService<TRecord, TPostParams, TPathParams> => {
        return async (
            pathParams: TPathParams,
            requestData?: TPostParams,
            signal?: AbortSignal
        ) =>
            _nestedCreateFromPostParams<TRecord, TPostParams, TPathParams>(
                recordType,
                resourceEndpoint,
                pathParams,
                requestData,
                signal
            );
    },

    /**
     * Creates conventional Service Update function for the supplied resource type
     *
     * Should be swapped with the @rsm-hcd/javascript-react version when https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/46 is complete
     *
     * @param recordType
     * @param resourceEndpoint
     */
    update<
        TRecord extends RecordType,
        TPathParams extends any,
        TQueryParams = undefined,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): UpdateServiceWithSignal<TRecord, TPathParams, TQueryParams> {
        return async (
            record: TRecord,
            pathParams?: TPathParams,
            queryParams?: TQueryParams,
            signal?: AbortSignal
        ) =>
            await _update<TRecord, TPathParams, TQueryParams>(
                recordType,
                record,
                resourceEndpoint,
                pathParams,
                queryParams,
                signal
            );
    },

    /**
     * Creates conventional Service Update function to update the supplied resource type from a set
     * of request body parameters.
     *
     * @param recordType
     * @param resourceEndpoint
     */
    updateFromPutParams<
        TRecord extends RecordType,
        TPutParams,
        TPathParams extends any,
        TQueryParams = undefined,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): UpdateFromPutParamsService<
        TRecord,
        TPutParams,
        TPathParams,
        TQueryParams
    > {
        return async (
            requestData?: TPutParams,
            pathParams?: TPathParams,
            queryParams?: TQueryParams,
            signal?: AbortSignal
        ) =>
            await _updateFromPutParams<
                TRecord,
                TPutParams,
                TPathParams,
                TQueryParams
            >(
                recordType,
                resourceEndpoint,
                requestData,
                pathParams,
                queryParams,
                signal
            );
    },

    /* Creates conventional Service Patch function for the supplied resource type
     *
     * @param recordType
     * @param resourceEndpoint
     */
    patch: <
        TRecord extends any,
        TPathParams = undefined,
        TPatchParams = undefined,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): PatchService<TRecord, TPathParams, TPatchParams> => {
        return async (
            pathParams?: TPathParams,
            values?: TPatchParams
        ): Promise<ServiceResponse<TRecord>> =>
            await _patch<TRecord, TPathParams, TPatchParams>(
                recordType,
                resourceEndpoint,
                pathParams,
                values
            );
    },

    /**
     * Creates conventional Service Create function for the supplied resource type
     */
    stream: <TRecord extends RecordType, TPathParams = undefined>(
        RecordType: new (props: Partial<TRecord>) => TRecord,
        resourceEndpoint: string
    ): StreamServiceWithSignal<TRecord, TPathParams> => {
        return (
            record: TRecord,
            pathParams?: TPathParams,
            signal?: AbortSignal
        ): ReadableStream<ServiceResponse<TRecord>> =>
            _stream<TRecord, TPathParams>(
                RecordType,
                resourceEndpoint,
                record,
                pathParams,
                signal
            );
    },
};

// #endregion Public Functions

// ---------------------------------------------------------------------------------------------
// #region Private Functions
// ---------------------------------------------------------------------------------------------

const _patch = async <
    TRecord extends any,
    TPathParams = undefined,
    TPatchParams = undefined,
>(
    recordType: { new (): TRecord },
    resourceEndpoint: string,
    pathParams?: TPathParams,
    values?: TPatchParams
): Promise<ServiceResponse<TRecord>> => {
    const url: string = RouteUtils.getUrl(resourceEndpoint, pathParams);

    return await axios
        .patch(url, values)
        .then((r) => ServiceUtils.mapAxiosResponse(recordType, r));
};

const _bulkCreate = async function <TRecord extends RecordType>(
    recordType: { new (): TRecord },
    url: string,
    records?: Array<TRecord>
) {
    const requestData =
        records != null ? records.map((record) => record.toJS()) : null;

    return await axios
        .post(url, requestData)
        .then((r) => ServiceUtils.mapPagedAxiosResponse(recordType, r));
};

// TODO: Move to open source.
// See https://github.com/AndcultureCode/AndcultureCode.JavaScript.React/issues/55 for more information.
const _batchedList = async function <
    TRecord,
    TQueryParams,
    TPathParams = undefined,
>(
    recordType: { new (): TRecord },
    baseEndpoint: string,
    property: keyof TQueryParams,
    size: number,
    queryParams: TQueryParams,
    pathParams?: TPathParams,
    signal?: AbortSignal
) {
    const values = queryParams[property];
    if (!(values instanceof Array) || CollectionUtils.isEmpty(values)) {
        return ServiceResponseFactory.successfulList<TRecord>();
    }

    // TODO: Swap for open source `chunk` method.
    // See https://github.com/AndcultureCode/AndcultureCode.JavaScript.Core/issues/86 for more information.
    const batches = _.chunk(values, size);

    const requests = batches.map((batchValues) => {
        const batchedParams = Object.assign({}, queryParams);
        batchedParams[property] = batchValues as any;

        // If this code is added to our open source efforts, then the calls to `nestedList`
        // and `list` can be removed to use the `_list` private method in
        // `AndcultureCodeServiceFactory`.
        if (pathParams != null) {
            return RsmServiceFactory.nestedList<
                TRecord,
                TPathParams,
                TQueryParams
            >(recordType, baseEndpoint)(pathParams, batchedParams, signal);
        }

        return RsmServiceFactory.list<TRecord, TQueryParams>(
            recordType,
            baseEndpoint
        )(batchedParams, signal);
    }) as Promise<ServiceResponse<TRecord>>[];

    const results = await PromiseUtils.toArray(requests);
    return ServiceResponseFactory.successfulList(results, values.length);
};

const _buildUrl = (
    id: RecordId,
    resourceEndpoint: string,
    pathParams?: any,
    queryParams?: any
) => {
    if (pathParams == null) {
        pathParams = {};
    }
    pathParams = Object.assign(pathParams, { id });
    return RouteUtils.getUrl(resourceEndpoint, pathParams, queryParams);
};

const _createFromPostParams = async function <
    TRecord extends RecordType,
    TPostParams,
>(
    recordType: new () => TRecord,
    resourceEndpoint: string,
    requestData?: TPostParams,
    signal?: AbortSignal
) {
    return axios
        .post(resourceEndpoint, requestData, { signal })
        .then((r) => ServiceUtils.mapAxiosResponse(recordType, r));
};

const _delete = async function <TPathParams extends any>(
    id: number | string,
    resourceEndpoint: string,
    pathParams?: TPathParams
) {
    const url = _buildUrl(id, resourceEndpoint, pathParams);
    return await axios
        .delete(url)
        .then((r) => ServiceUtils.mapAxiosResponse(Boolean, r))
        .catch((e: AxiosError) => {
            return { status: 401, resultObjects: [], rowCount: 0 };
        });
};

const _bulkDelete = async function <
    TPathParams extends any,
    TQueryParams extends any,
>(
    resourceEndpoint: string,
    pathParams?: TPathParams,
    queryParams?: TQueryParams
) {
    const url = RouteUtils.getUrl(resourceEndpoint, pathParams, queryParams);

    return await axios
        .delete(url)
        .then((r) => ServiceUtils.mapAxiosResponse(Boolean, r))
        .catch((e: AxiosError) => {
            return { status: 401, resultObjects: [], rowCount: 0 };
        });
};

const _nestedCreateFromPostParams = async function <
    TRecord extends RecordType,
    TPostParams,
    TPathParams,
>(
    recordType: new () => TRecord,
    resourceEndpoint: string,
    pathParams: TPathParams,
    requestData?: TPostParams,
    signal?: AbortSignal
) {
    const url = RouteUtils.getUrl(resourceEndpoint, pathParams);

    return axios
        .post(url, requestData, { signal })
        .then((r) => ServiceUtils.mapAxiosResponse(recordType, r));
};

const _update = async function <
    TRecord extends RecordType,
    TPathParams extends any,
    TQueryParams extends any = any,
>(
    recordType: { new (): TRecord },
    record: TRecord,
    resourceEndpoint: string,
    pathParams?: TPathParams,
    queryParams?: TQueryParams,
    signal?: AbortSignal
) {
    const url = _buildUrl(record.id, resourceEndpoint, pathParams, queryParams);
    return await axios
        .put(url, record.toJS(), { signal })
        .then((r) => ServiceUtils.mapAxiosResponse(recordType, r));
};

const _updateFromPutParams = async function <
    TRecord extends RecordType,
    TPutParams,
    TPathParams extends any,
    TQueryParams extends any = any,
>(
    recordType: { new (): TRecord },
    resourceEndpoint: string,
    requestData?: TPutParams,
    pathParams?: TPathParams,
    queryParams?: TQueryParams,
    signal?: AbortSignal
) {
    const url = RouteUtils.getUrl(resourceEndpoint, pathParams, queryParams);

    return await axios
        .put(url, requestData, { signal })
        .then((r) => ServiceUtils.mapAxiosResponse(recordType, r));
};

const _stream = <TRecord extends RecordType, TPathParams = undefined>(
    RecordType: new (props: Partial<TRecord>) => TRecord,
    resourceEndpoint: string,
    record: TRecord,
    pathParams?: TPathParams,
    signal?: AbortSignal
): ReadableStream<ServiceResponse<TRecord>> => {
    const url: string = RouteUtils.getUrl(resourceEndpoint, pathParams);

    const readableStream = new ReadableStream<ServiceResponse<TRecord>>({
        async start(controller) {
            try {
                const response = await axios.post(url, record.toJS(), {
                    headers: {
                        Accept: "text/event-stream",
                    },
                    responseType: "stream",
                    adapter: "fetch",
                    signal: signal,
                });

                if (!(response.data instanceof ReadableStream)) {
                    throw new Error(
                        "Response data is not a stream. Ensure the endpoint supports streaming."
                    );
                }

                const reader = response.data.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    let split: number;
                    while ((split = buffer.indexOf("\n\n")) !== -1) {
                        const chunk = buffer.slice(0, split);
                        buffer = buffer.slice(split + 2);

                        if (!chunk.startsWith("data:")) continue;

                        const data = chunk.slice(5).trim();

                        if (data === "[DONE]") {
                            return;
                        }

                        const json = JSON.parse(data);

                        controller.enqueue({
                            result: new ResultRecord<TRecord>({
                                resultObject: json,
                            }),
                            resultObject: new RecordType(json),
                            resultObjects: [],
                            rowCount: 1,
                            status: response.status,
                        });
                    }
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    controller.error(
                        new Error("Request was cancelled by the user.")
                    );
                } else {
                    controller.error(error);
                }
            } finally {
                controller.close();
            }
        },
    });

    return readableStream;
};

// #endregion Private Functions

// ---------------------------------------------------------------------------------------------
// #region Exports
// ---------------------------------------------------------------------------------------------

export default ServiceFactory;

// #endregion Exports
